import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { Volunteer, VolunteerDay } from '@nepal-football/shared';

import { useCreateVolunteer, useTasks, useUpdateVolunteer } from '../api/hooks';
import { ApiClientError } from '../api/client';

const DAYS: VolunteerDay[] = ['Friday', 'Saturday', 'Sunday'];

export interface VolunteerFormProps {
  /** When provided, the form opens in edit mode. */
  volunteer?: Volunteer;
  onSave: (v: Volunteer) => void;
  onCancel: () => void;
}

export default function VolunteerForm({ volunteer, onSave, onCancel }: VolunteerFormProps) {
  const isEdit = volunteer !== undefined;
  const createMutation = useCreateVolunteer();
  const updateMutation = useUpdateVolunteer();
  const { data: tasks } = useTasks();
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Filter stale task references from the pre-populated selection so an admin
  // editing a volunteer doesn't see a checkbox tied to a deleted task.
  const initialTaskIds = useMemo(() => {
    if (!volunteer?.taskIds) return [] as string[];
    if (!tasks) return volunteer.taskIds;
    const known = new Set(tasks.map((t) => t.id));
    return volunteer.taskIds.filter((id) => known.has(id));
  }, [volunteer, tasks]);

  const [name,  setName]  = useState(volunteer?.name  ?? '');
  const [email, setEmail] = useState(volunteer?.email ?? '');
  const [phone, setPhone] = useState(volunteer?.phone ?? '');
  const [city,  setCity]  = useState(volunteer?.city  ?? '');
  const [notes, setNotes] = useState(volunteer?.notes ?? '');
  const [days,  setDays]  = useState<Set<VolunteerDay>>(new Set(volunteer?.days ?? []));
  const [taskIds, setTaskIds] = useState<Set<string>>(new Set(initialTaskIds));
  const [errors, setErrors] = useState<{ name?: string; days?: string; submit?: string }>({});

  // If tasks load after the form mounts, prune stale refs in edit mode.
  useEffect(() => {
    if (!volunteer || !tasks) return;
    const known = new Set(tasks.map((t) => t.id));
    setTaskIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) if (known.has(id)) next.add(id);
      return next;
    });
  }, [tasks, volunteer]);

  function toggleDay(day: VolunteerDay) {
    setDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  }

  function toggleTask(id: string) {
    setTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const validation: typeof errors = {};
    const trimmedName = name.trim();
    if (trimmedName.length === 0) validation.name = 'Name is required';
    if (days.size === 0) validation.days = 'Select at least one day';
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setErrors({});

    const payload = {
      name: trimmedName,
      days: Array.from(days),
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      city:  city.trim()  || undefined,
      notes: notes.trim() || undefined,
      taskIds: Array.from(taskIds),
    };

    try {
      const result = isEdit
        ? await updateMutation.mutateAsync({ id: volunteer!.id, ...payload })
        : await createMutation.mutateAsync(payload);
      onSave(result);
    } catch (err) {
      const message =
        err instanceof ApiClientError ? err.message : 'Could not save volunteer.';
      setErrors({ submit: message });
    }
  }

  return (
    <form className="volunteer-form" onSubmit={handleSubmit} noValidate>
      <div className="modal-header">
        <span className="modal-title">
          {isEdit ? '✏️ Edit Volunteer' : '➕ Add Volunteer'}
        </span>
        <button
          type="button"
          className="modal-close"
          onClick={onCancel}
          aria-label="Close form"
        >
          ✕
        </button>
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="vol-name">Name *</label>
        <input
          id="vol-name"
          className="reg-input"
          type="text"
          maxLength={100}
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? 'vol-name-error' : undefined}
          required
        />
        {errors.name && (
          <div id="vol-name-error" className="reg-error" role="alert">
            {errors.name}
          </div>
        )}
      </div>

      <fieldset className="reg-field">
        <legend className="reg-label">Available days *</legend>
        <div className="vol-days-row">
          {DAYS.map((day) => (
            <label key={day} className="checkbox-label">
              <input
                type="checkbox"
                checked={days.has(day)}
                onChange={() => toggleDay(day)}
              />
              {day}
            </label>
          ))}
        </div>
        {errors.days && (
          <div className="reg-error" role="alert">{errors.days}</div>
        )}
      </fieldset>

      <div className="reg-field">
        <label className="reg-label" htmlFor="vol-email">Email</label>
        <input
          id="vol-email"
          className="reg-input"
          type="email"
          maxLength={200}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="vol-phone">Phone</label>
        <input
          id="vol-phone"
          className="reg-input"
          type="tel"
          maxLength={30}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="vol-city">City</label>
        <input
          id="vol-city"
          className="reg-input"
          type="text"
          maxLength={100}
          value={city}
          onChange={(e) => setCity(e.target.value)}
        />
      </div>

      <fieldset className="reg-field">
        <legend className="reg-label">Assigned tasks</legend>
        {!tasks && <div className="inline-loading">Loading tasks…</div>}
        {tasks && tasks.length === 0 && (
          <div className="muted">No tasks defined yet.</div>
        )}
        {tasks && tasks.length > 0 && (
          <div className="task-checkbox-grid">
            {tasks.map((t) => (
              <label key={t.id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={taskIds.has(t.id)}
                  onChange={() => toggleTask(t.id)}
                />
                <span>
                  {t.task}
                  <span className="muted"> ({t.name})</span>
                </span>
              </label>
            ))}
          </div>
        )}
      </fieldset>

      <div className="reg-field">
        <label className="reg-label" htmlFor="vol-notes">Notes</label>
        <textarea
          id="vol-notes"
          className="reg-input"
          rows={3}
          maxLength={500}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {errors.submit && (
        <div className="reg-error" role="alert" style={{ marginBottom: 12 }}>
          ⚠️ {errors.submit}
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          className="reg-btn reg-btn-ghost"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="reg-btn"
          disabled={isPending}
          aria-busy={isPending}
        >
          {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add volunteer'}
        </button>
      </div>
    </form>
  );
}
